import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, CirclePlus } from "lucide-react";
import NavBar from "./Navbar";
import TopNavBar from "./TopNavbar";
import { Badge } from "./ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { fetchAccounts, deleteAccount } from "../services/AccountService";
import {
  ROLES,
  canManageAccounts,
  formatAccountId,
  getAccountId,
  roleLabel,
  statusLabel,
  PERMISSIONS,
} from "../utils/rbac";
import {
  deniedActionClass,
  usePermissionGuard,
} from "../hooks/usePermissionGuard";
import "../styles/ManageAccounts.css";

function formatLastLogin(value) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const startOfThatDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const dayDiff = Math.round(
    (startOfToday.getTime() - startOfThatDay.getTime()) / (1000 * 60 * 60 * 24),
  );

  const timeStr = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

  if (dayDiff === 0) return `Today, ${timeStr}`;
  if (dayDiff === 1) return "Yesterday";
  if (dayDiff > 1 && dayDiff < 7) return `${dayDiff} days ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function roleBadgeClass(role) {
  switch ((role || "").toLowerCase()) {
    case ROLES.ADMIN:
      return "bg-[#DCFCE7] text-[#166534] font-bold";
    case ROLES.STAFF:
      return "bg-[#DBEAFE] text-[#1D4ED8] font-bold";
    case ROLES.VIEWER:
      return "bg-[#F3F4F6] text-[#374151] font-bold";
    default:
      return "bg-[#F3F4F6] text-[#374151] font-bold";
  }
}

function statusBadgeClass(status) {
  return (status || "").toLowerCase() === "active"
    ? "bg-[#22C55E] text-white font-bold"
    : "bg-[#9CA3AF] text-white font-bold";
}

function ManageAccounts() {
  const navigate = useNavigate();
  const [roleReady, setRoleReady] = useState(
    !!localStorage.getItem("user_role"),
  );
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const loggedInAccountId = getAccountId();
  const { guardAction, PermissionDialog, isAllowed } = usePermissionGuard();
  const canManage = isAllowed(PERMISSIONS.MANAGE_ACCOUNTS);

  useEffect(() => {
    if (roleReady && !canManage) {
      guardAction(PERMISSIONS.MANAGE_ACCOUNTS, "manage accounts");
    }
  }, [roleReady, canManage, guardAction]);

  useEffect(() => {
    if (localStorage.getItem("user_role")) {
      setRoleReady(true);
      return undefined;
    }
    const onSessionUpdated = () => {
      if (localStorage.getItem("user_role")) setRoleReady(true);
    };
    window.addEventListener("user-session-updated", onSessionUpdated);
    const timer = setInterval(() => {
      if (localStorage.getItem("user_role")) {
        setRoleReady(true);
        clearInterval(timer);
      }
    }, 100);
    const timeout = setTimeout(() => {
      setRoleReady(true);
      clearInterval(timer);
    }, 3000);
    return () => {
      window.removeEventListener("user-session-updated", onSessionUpdated);
      clearInterval(timer);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await fetchAccounts(params);
      if (res?.status === 1) {
        setAccounts(Array.isArray(res.data) ? res.data : []);
      } else {
        setLoadError(res?.message || "Failed to load accounts");
      }
    } catch (err) {
      setLoadError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to load accounts",
      );
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (roleReady && canManage) {
      loadAccounts();
    }
  }, [loadAccounts, roleReady, canManage]);

  const filteredAccounts = useMemo(() => accounts, [accounts]);

  if (!roleReady) {
    return (
      <div className="admin-panel-parent-container">
        <NavBar />
        <TopNavBar />
        <div className="manage-accounts-page-wrapper ml-[15%] mt-[66px] px-4 py-12 bg-gray-100 min-h-[calc(100vh-66px)]">
          <p className="text-primary text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteAccount(deleteTarget.accountId);
      if (res?.status !== 1) throw new Error(res?.message || "Delete failed");
      toast.success("Account deleted successfully");
      setDeleteTarget(null);
      loadAccounts();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err.message ||
          "Failed to delete account",
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="admin-panel-parent-container">
      <NavBar />
      <TopNavBar />
      <PermissionDialog />
      <div
        className={`manage-accounts-page-wrapper ml-[15%] mt-[66px] px-4 sm:px-6 lg:px-8 py-12 sm:py-6 bg-gray-100 min-h-[calc(100vh-66px)] ${!canManage ? "pointer-events-none opacity-60" : ""}`}
      >
        <div className="text-sm mb-4">
          <Link
            to="/dashboard"
            className="text-primary no-underline hover:underline"
          >
            Dashboard
          </Link>
          <span className="text-primary mx-2">&gt;</span>
          <span className="text-primary font-bold">Manage Accounts</span>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-[#2F7F571A] overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-5 sm:p-6 border-b border-[#2F7F571A]">
            <h1 className="text-2xl sm:text-3xl font-black text-[#131614] m-0 ml-0">
              Manage Accounts
            </h1>
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <div className="relative flex items-center min-w-0 sm:min-w-[220px]">
                <svg
                  className="absolute left-3 w-4 h-4 text-primary pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Search"
                  className="w-full py-2 px-3 pl-10 border border-[#2F7F571A] rounded-lg text-sm outline-none bg-[#F6F8F7] text-primary placeholder:text-[#2F7F574D] focus:border-[#2F7F57]"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Link
                to="/manageAccounts/add"
                className={`flex items-center justify-center gap-1 py-1.5 px-4 border-2 border-[#2F7F57] text-[#2F7F57] bg-white rounded-lg font-semibold text-sm no-underline transition-colors hover:bg-[#F0F7F3] whitespace-nowrap ${deniedActionClass(canManage)}`}
                onClick={(e) => {
                  e.preventDefault();
                  guardAction(PERMISSIONS.MANAGE_ACCOUNTS, "add account", () => {
                    navigate("/manageAccounts/add");
                  });
                }}
              >
                <CirclePlus className="w-6 h-6 text-white fill-[#2F7F57]" />
                Add Account
              </Link>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] border-collapse">
              <thead>
                <tr>
                  {[
                    "Account ID",
                    "Profile",
                    "Full Name",
                    "Email Address",
                    "Role",
                    "Status",
                    "Last Login",
                    "Actions",
                  ].map((label) => (
                    <th
                      key={label}
                      className="p-4 text-left text-xs font-semibold text-white uppercase tracking-wide bg-[#2E8056] border-b border-[#256845]"
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td className="p-4 text-sm text-primary" colSpan={8}>
                      Loading...
                    </td>
                  </tr>
                ) : loadError ? (
                  <tr>
                    <td className="p-4 text-sm text-red-600" colSpan={8}>
                      {loadError}
                    </td>
                  </tr>
                ) : filteredAccounts.length === 0 ? (
                  <tr>
                    <td className="p-4 text-sm text-primary" colSpan={8}>
                      No accounts found
                    </td>
                  </tr>
                ) : (
                  filteredAccounts.map((account) => (
                    <tr
                      key={account.accountId}
                      className="border-b border-[#2F7F571A] transition-colors hover:bg-gray-50 last:border-b-0"
                    >
                      <td className="p-4 text-sm font-semibold text-[#2F7F57]">
                        {formatAccountId(account.accountId)}
                      </td>
                      <td className="p-4">
                        <div className="w-9 h-9 rounded-full overflow-hidden bg-[#E8F2EC] flex items-center justify-center">
                          {account.profile_picture ? (
                            <img
                              src={account.profile_picture}
                              alt={account.fullname}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-xs font-bold text-[#2F7F57]">
                              {(account.fullname || "?")
                                .charAt(0)
                                .toUpperCase()}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-sm font-medium text-[#131614]">
                        {account.fullname}
                      </td>
                      <td className="p-4 text-sm text-[#6B7280]">
                        {account.email}
                      </td>
                      <td className="p-4 text-base">
                        <Badge
                          className={`${roleBadgeClass(account.role)} normal-case tracking-normal rounded-md shadow-none`}
                        >
                          {roleLabel(account.role)}
                        </Badge>
                      </td>
                      <td className="p-4 text-base">
                        <Badge
                          className={`${statusBadgeClass(account.status)} normal-case tracking-normal shadow-none`}
                        >
                          {statusLabel(account.status)}
                        </Badge>
                      </td>
                      <td className="p-4 text-sm text-[#6B7280]">
                        {formatLastLogin(account.last_login)}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2 items-center">
                          <Link
                            to={`/manageAccounts/edit/${account.accountId}`}
                            title="Edit"
                            className={`p-1.5 bg-transparent border-none text-primary cursor-pointer rounded transition-all flex items-center justify-center hover:bg-gray-100 hover:text-primary no-underline ${deniedActionClass(canManage)}`}
                            onClick={(e) => {
                              e.preventDefault();
                              guardAction(
                                PERMISSIONS.MANAGE_ACCOUNTS,
                                "edit account",
                                () => {
                                  navigate(
                                    `/manageAccounts/edit/${account.accountId}`,
                                  );
                                },
                              );
                            }}
                          >
                            <Pencil className="w-5 h-5" />
                          </Link>
                          {String(loggedInAccountId) !==
                          String(account.accountId) ? (
                            <button
                              type="button"
                              title="Delete"
                              className={`p-1.5 bg-transparent border-none cursor-pointer rounded transition-all flex items-center justify-center hover:bg-red-100/50 ${deniedActionClass(canManage)}`}
                              onClick={() => {
                                guardAction(
                                  PERMISSIONS.MANAGE_ACCOUNTS,
                                  "delete account",
                                  () => setDeleteTarget(account),
                                );
                              }}
                            >
                              <Trash2 className="w-5 h-5 text-red-600" />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent className="z-[9999]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-black ml-0">
              Delete account?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-black">
              This will soft-delete&nbsp;
              <span className="font-bold">
                {deleteTarget?.fullname || "this account"}
              </span>
              . They will no longer be able to sign in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-black" disabled={deleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default ManageAccounts;
